import React, { useEffect, useState } from "react";
import { checkValidObject, handleServerResponse, saveEditForm, useSafeFormikContext } from "GenericForms/Helper";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import env from "environment_system/env_system";
import { useFormik } from "formik";
import apiGlobal from "global/api.global";
import { RootState } from "index";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Form,
  Label,
  Button,
  CardHeader,
  Card,
  CardBody,
  CardFooter,
  Input,
} from "reactstrap";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import FormLoading from "Components/FormLoading";
import ErrorComponent from "Components/ErrorComponent";
import * as Yup from "yup";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import ErrorTooltip from "Components/ErrorTooltip";

interface TugsUsageComponentType {
  activeAddTab: number;
  toggleAddTab: any;
  setErrorMessage: (value: boolean) => void,
  editReportBool: boolean,
}

const TugsUsageComponent = ({
  activeAddTab,
  toggleAddTab,
  setErrorMessage,
  editReportBool,
}: TugsUsageComponentType) => {
  /** State variables start*/
  const { VesselID, VoyageID, ReportID, CurrentReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
  const tugsUsageCurrentReport = CurrentReport?.data?.deck_officer?.lfonds_tugs_usage ?? null;
  const dispatch = useDispatch();
  const [refreshKey, setRefreshKey] = useState(0);
  const formikContext = useSafeFormikContext<any>();
  const setEditValues = formikContext?.setValues;
  const editInitialValues = formikContext?.initialValues;
  const editSubmit = formikContext?.submitForm;
  const editIsSubmitting = formikContext?.isSubmitting;
  /** State variables end */

  /** Assign values to initial object of Tugs Usage */
  const getInitialTugsUsageValues = () => {
    if (checkValidObject(editInitialValues?.deck_officer?.lfonds_tugs_usage)) {
      return editInitialValues?.deck_officer?.lfonds_tugs_usage;
    } else if (checkValidObject(tugsUsageCurrentReport)) {
      return tugsUsageCurrentReport;
    } else {
      return {
        number_of_tugs: 0,
        remarks: "",
        vessel: VesselID,
        voyage_information: VoyageID,
        vessel_reporting_information: ReportID,
      };
    }
  };

  /** Tugs Usage formik object */
  const TugsUsageFormik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialTugsUsageValues(),
    validationSchema: Yup.object().shape({
      number_of_tugs: Yup.number().nullable().typeError(`${commonValidationMessages.number}`),
      remarks: Yup.string().max(250, commonValidationMessages.max250Char).nullable(),
    }),
    onSubmit: async (values: any, actions: any) => {
      if (env?.form_validation === false) {
        setErrorMessage(null)
        toggleAddTab(activeAddTab + 1);
      }
      actions.setSubmitting(true);
      let responseArray: any = [];
      if (checkValidObject(tugsUsageCurrentReport)) {
        responseArray.push(apiGlobal.put(`/lfonds_tug_usage/${values?.id}/`, values));
      } else {
        responseArray.push(apiGlobal.post(`/lfonds_tug_usage/`, values));
      }
      await handleServerResponse(responseArray).then(async (res: any) => {
        if (res === true) {
          dispatch(getCurrentReportAction(VesselID, ReportID));
          setRefreshKey(refreshKey + 1);
          if (env?.form_validation === true) {
            setErrorMessage(null)
            toggleAddTab(activeAddTab + 1);
          }
        } else {
          setErrorMessage(res)
        }
      })
      actions.setSubmitting(false);
    }
  });

  /** useEffect */
  useEffect(() => {
    TugsUsageFormik.initialValues = getInitialTugsUsageValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tugsUsageCurrentReport])
  useEffect(() => {
    if (checkValidObject(editInitialValues?.deck_officer?.lfonds_tugs_usage)) {
      setEditValues({
        ...editInitialValues,
        deck_officer: {
          ...editInitialValues?.deck_officer,
          lfonds_tugs_usage: TugsUsageFormik?.values
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TugsUsageFormik?.values])
  /** useEffect end */
  return (
    <Card className="p-0 mb-0">
      <CardHeader className="p-2">
        <div className="text-center">
          <Row>
            <Col>
              <h4 className="page_title pos-start mb-0">Tugs Usage</h4>
              <p className="card-title-desc pos-start">
                All readings since last report
              </p>
            </Col>
          </Row>
        </div>
      </CardHeader>
      {reportingLoading && <FormLoading />}
      {reportingError && <ErrorComponent message="Error loading required data!" />}
      {!reportingLoading && !reportingError && (
        <Form autoComplete="off" onSubmit={TugsUsageFormik?.handleSubmit} noValidate>
          <CardBody className="px-2 py-0 mt-2">
            <Row className="mb-3">
              <Col sm={3}>
                <Label className="mb-0" for="number_of_tugs">
                  Number of tugs used
                </Label>
                <Input
                  onFocus={(e) => e.target.select()}
                  type="text"
                  autoFocus={true}
                  name="number_of_tugs"
                  id="number_of_tugs"
                  className="form-control max-width-13"
                  value={TugsUsageFormik?.values?.number_of_tugs}
                  onChange={TugsUsageFormik?.handleChange}
                  onBlur={TugsUsageFormik?.handleBlur}
                  defaultValue={TugsUsageFormik?.values?.number_of_tugs}
                />
                {TugsUsageFormik?.errors?.number_of_tugs &&
                  TugsUsageFormik?.touched?.number_of_tugs &&
                  env?.form_validation === true &&
                  <ErrorTooltip
                    target={`number_of_tugs`}
                    message={TugsUsageFormik?.errors?.number_of_tugs}
                    open={!!TugsUsageFormik?.errors.number_of_tugs}
                  />
                }
              </Col>
              <Col sm={4}>
                <Label className="mb-0" for="remarks">
                  Remarks
                </Label>
                <Input
                  onFocus={(e) => e.target.select()}
                  as="textarea"
                  name="remarks"
                  id="remarks"
                  className="form-control"
                  rows={3}
                  onChange={TugsUsageFormik?.handleChange}
                  onBlur={TugsUsageFormik?.handleBlur}
                  defaultValue={TugsUsageFormik?.values?.remarks}
                />
                {TugsUsageFormik?.errors?.remarks &&
                  TugsUsageFormik?.touched?.remarks &&
                  env?.form_validation === true &&
                  <ErrorTooltip
                    target={`remarks`}
                    message={TugsUsageFormik?.errors?.remarks}
                    open={!!TugsUsageFormik?.errors.remarks}
                  />
                }
              </Col>
            </Row>
          </CardBody>
          <CardFooter className="p-2 py-3">
            <Row className="d-flex align-items-center flex-row-reverse">
              <Col lg={10}>
                <div className="d-flex flex-wrap gap-3 justify-content-end ">
                  {/* {editReportBool && (
                    <Button
                      type="reset"
                      color="danger"
                      className="btn_size_cstm"
                      onClick={() => {
                        setEditValues({
                          ...editValues,
                          deck_officer: {
                            ...editValues?.deck_officer,
                            lfonds_tugs_usage: editInitialValues?.deck_officer?.lfonds_tugs_usage,
                          }
                        });
                      }}
                    >
                      Reset
                    </Button>
                  )} */}
                  <Button
                    type={editReportBool ? "button" : "submit"}
                    color="primary"
                    className="btn_size_cstm"
                    disabled={TugsUsageFormik?.isSubmitting || editIsSubmitting}
                    onClick={async () => {
                      const validationErrors = await TugsUsageFormik?.validateForm();
                      Object.keys(TugsUsageFormik?.values || {}).forEach((key: any) => {
                        TugsUsageFormik?.setFieldTouched(key, true);
                      });
                      if (editReportBool && Object.keys(validationErrors).length === 0) {
                        await saveEditForm(editSubmit, toggleAddTab, activeAddTab);
                      }
                    }}
                  >
                    {(TugsUsageFormik?.isSubmitting || editIsSubmitting) && (
                      <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                    )}
                    Next <i className="bx bx-chevron-right ms-1" />
                  </Button>
                </div>
              </Col>
              <Col lg={2}>
                <Button
                  type="button"
                  color="primary"
                  className="btn_size_cstm"
                  onClick={() => {
                    TugsUsageFormik?.setErrors({});
                    setErrorMessage(null);
                    toggleAddTab(activeAddTab - 1);
                  }}
                >
                  <i className="bx bx-chevron-left me-1" /> Previous
                </Button>
              </Col>
            </Row>
          </CardFooter>
          <FormValuesDebug
            values={[TugsUsageFormik?.values, TugsUsageFormik?.errors, TugsUsageFormik.initialValues, TugsUsageFormik.touched]}
          />
        </Form>
      )}
    </Card>
  );
};

export default TugsUsageComponent;
